// Presentation helpers shared by the Storm Center widgets. Pure: no React,
// no network, so they are unit-tested directly.

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

export function compass(deg) {
  const n = Number(deg);
  if (!Number.isFinite(n)) return "";
  return COMPASS[Math.round((((n % 360) + 360) % 360) / 22.5) % 16];
}

export const THREAT = {
  tornado: { label: "TORNADO", color: "#d946ef", rank: 4 },
  severe: { label: "SEVERE", color: "#ef4444", rank: 3 },
  strong: { label: "STRONG", color: "#f97316", rank: 2 },
  moderate: { label: "MOD", color: "#22c55e", rank: 1 },
};
export const threatOf = (t) => THREAT[t] || THREAT.moderate;

// NWS's own hazard-map colors, so a board matches what the weather service
// and TV stations show. Rank orders the list and the map (higher on top);
// alert types not listed are ranked by kind and drawn in slate.
const ALERT_STYLE = {
  "Tornado Warning": { color: "#ff0000", rank: 100 },
  "Extreme Wind Warning": { color: "#ff8c00", rank: 95 },
  "Severe Thunderstorm Warning": { color: "#ffa500", rank: 90 },
  "Flash Flood Warning": { color: "#8b0000", rank: 85 },
  "Snow Squall Warning": { color: "#c71585", rank: 80 },
  "Hurricane Warning": { color: "#dc143c", rank: 78 },
  "Storm Surge Warning": { color: "#b524f7", rank: 77 },
  "Tropical Storm Warning": { color: "#b22222", rank: 76 },
  "Tornado Watch": { color: "#ffff00", rank: 70 },
  "Severe Thunderstorm Watch": { color: "#db7093", rank: 65 },
  "Special Marine Warning": { color: "#ffa500", rank: 60 },
  "Blizzard Warning": { color: "#ff4500", rank: 55 },
  "Ice Storm Warning": { color: "#8b008b", rank: 54 },
  "Winter Storm Warning": { color: "#ff69b4", rank: 53 },
  "Flood Warning": { color: "#00ff00", rank: 50 },
  "High Wind Warning": { color: "#daa520", rank: 49 },
  "Extreme Heat Warning": { color: "#c71585", rank: 48 },
  "Excessive Heat Warning": { color: "#c71585", rank: 48 },
  "Extreme Cold Warning": { color: "#0000ff", rank: 48 },
  "Red Flag Warning": { color: "#ff1493", rank: 47 },
  "Lake Effect Snow Warning": { color: "#008b8b", rank: 46 },
  "Dust Storm Warning": { color: "#ffe4c4", rank: 46 },
  "Coastal Flood Warning": { color: "#228b22", rank: 45 },
  "Freeze Warning": { color: "#483d8b", rank: 42 },
  "Hurricane Watch": { color: "#ff00ff", rank: 40 },
  "Tropical Storm Watch": { color: "#f08080", rank: 39 },
  "Flash Flood Watch": { color: "#2e8b57", rank: 38 },
  "Flood Watch": { color: "#2e8b57", rank: 37 },
  "Winter Storm Watch": { color: "#4682b4", rank: 36 },
  "High Wind Watch": { color: "#b8860b", rank: 35 },
  "Fire Weather Watch": { color: "#ffdead", rank: 34 },
  "Extreme Heat Watch": { color: "#800000", rank: 33 },
  "Extreme Cold Watch": { color: "#5f9ea0", rank: 33 },
  "Freeze Watch": { color: "#00ffff", rank: 32 },
  "Special Weather Statement": { color: "#ffe4b5", rank: 20 },
  "Winter Weather Advisory": { color: "#7b68ee", rank: 19 },
  "Wind Advisory": { color: "#d2b48c", rank: 18 },
  "Flood Advisory": { color: "#00ff7f", rank: 18 },
  "Heat Advisory": { color: "#ff7f50", rank: 17 },
  "Cold Weather Advisory": { color: "#afeeee", rank: 16 },
  "Wind Chill Advisory": { color: "#afeeee", rank: 16 },
  "Dense Fog Advisory": { color: "#708090", rank: 15 },
  "Frost Advisory": { color: "#6495ed", rank: 14 },
  "Blowing Dust Advisory": { color: "#bdb76b", rank: 14 },
  "Coastal Flood Advisory": { color: "#7cfc00", rank: 14 },
  "Lake Wind Advisory": { color: "#d2b48c", rank: 13 },
  "Small Craft Advisory": { color: "#d8bfd8", rank: 12 },
  "Air Quality Alert": { color: "#808080", rank: 11 },
  "Severe Weather Statement": { color: "#00ffff", rank: 10 },
  "Hazardous Weather Outlook": { color: "#eee8aa", rank: 5 },
};

// "warning" | "watch" | "advisory" | "statement" | "other", from the event name.
export function alertKind(event = "") {
  const e = String(event);
  if (/Warning$/.test(e)) return "warning";
  if (/Watch$/.test(e)) return "watch";
  if (/Advisory$|Alert$/.test(e)) return "advisory";
  if (/Statement$|Outlook$/.test(e)) return "statement";
  return "other";
}
const KIND_RANK = { warning: 45, watch: 30, advisory: 12, statement: 8, other: 4 };

export function alertStyle(event) {
  return ALERT_STYLE[event] || { color: "#94a3b8", rank: KIND_RANK[alertKind(event)] };
}
export function sortAlerts(alerts = []) {
  return [...alerts].sort(
    (a, b) =>
      (b.affectsStation ? 1 : 0) - (a.affectsStation ? 1 : 0) ||
      alertStyle(b.event).rank - alertStyle(a.event).rank ||
      (a.expiresMs || 0) - (b.expiresMs || 0)
  );
}

// Buckets a free-text report type ("Tstm Wnd Gst", "Hail", "Funnel Cloud",
// mPING's "Hail 1.00 inch", the Ladder app's "Mixed Precipitation"...) into
// the handful of symbols the map draws.
export function reportKind(type = "") {
  const t = String(type).toLowerCase();
  if (/tornado|funnel|wall cloud/.test(t)) return "tornado";
  if (/hail/.test(t)) return "hail";
  if (/flood|water/.test(t)) return "flood";
  if (/wind|gust|tstm|damage|tree|power line|wires/.test(t)) return "wind";
  if (/snow|sleet|ice|freezing|mixed/.test(t)) return "winter";
  if (/lightning/.test(t)) return "lightning";
  if (/fog/.test(t)) return "fog";
  if (/rain|shower|precip/.test(t)) return "rain";
  return "other";
}

// Who a report came from, as the boards label it.
export function reportSource(source) {
  return source === "mping" ? "mPING" : source === "ladder" ? "LADDER" : "NWS";
}
export const REPORT_STYLE = {
  tornado: { color: "#d946ef", glyph: "T" },
  hail: { color: "#22d3ee", glyph: "H" },
  wind: { color: "#60a5fa", glyph: "W" },
  flood: { color: "#34d399", glyph: "F" },
  winter: { color: "#e2e8f0", glyph: "S" },
  rain: { color: "#3b82f6", glyph: "R" },
  lightning: { color: "#fde047", glyph: "L" },
  fog: { color: "#94a3b8", glyph: "≡" },
  other: { color: "#facc15", glyph: "•" },
};

export function reportMagnitude(r) {
  if (r.magnitude == null || r.magnitude === "") return "";
  const unit = String(r.unit || "").toLowerCase();
  const kind = reportKind(r.type);
  if (kind === "hail" || unit === "inch" || unit === "in") return `${r.magnitude}″`;
  if (kind === "wind" || /mph|kt|knot/.test(unit)) return `${r.magnitude} ${unit.startsWith("k") ? "kt" : "mph"}`;
  return `${r.magnitude}${unit ? ` ${unit}` : ""}`;
}

export function filterReports(reports = [], { hours = 3, sources = "all", now = Date.now() } = {}) {
  const since = now - Number(hours || 3) * 3600 * 1000;
  return reports.filter(
    (r) => (r.timeMs || 0) >= since && (sources === "all" || !sources || r.source === sources)
  );
}

export function sortCells(cells = [], { approachingFirst = true } = {}) {
  return [...cells].sort((a, b) => {
    if (approachingFirst) {
      const aa = a.approaching ? 1 : 0;
      const bb = b.approaching ? 1 : 0;
      if (aa !== bb) return bb - aa;
      if (aa && bb && (a.etaMin ?? 1e9) !== (b.etaMin ?? 1e9)) return (a.etaMin ?? 1e9) - (b.etaMin ?? 1e9);
    }
    return threatOf(b.threat).rank - threatOf(a.threat).rank || (a.distanceMi ?? 1e9) - (b.distanceMi ?? 1e9);
  });
}

export function relTime(ms, now = Date.now()) {
  if (!ms) return "";
  const min = Math.round((now - ms) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m ago`;
}

export function clockTime(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function distanceMi(lat1, lng1, lat2, lng2) {
  const r = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * r) / 2) ** 2 +
    Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(((lng2 - lng1) * r) / 2) ** 2;
  return 2 * 3958.7613 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Hazard reports from the Ladder app (WXREPORTS docs: { condition, comment,
// timestamp, location: { latitude, longitude } }) as storm report rows near
// the station, newest first. The same rows getstormdata builds server-side;
// used when the board talks to an older getstormdata without them.
export function ladderReportRows(docs, { lat, lng, radiusMi, hours, now = Date.now() }) {
  const since = now - hours * 3600 * 1000;
  const toMs = (t) =>
    t && typeof t.toMillis === "function" ? t.toMillis()
      : t instanceof Date ? t.getTime()
        : typeof t === "number" ? t
          : null;
  const rows = [];
  for (const d of docs || []) {
    const rLat = Number(d?.location?.latitude);
    const rLng = Number(d?.location?.longitude);
    if (!Number.isFinite(rLat) || !Number.isFinite(rLng) || (rLat === 0 && rLng === 0)) continue;
    const dist = distanceMi(lat, lng, rLat, rLng);
    const timeMs = toMs(d.timestamp);
    if (dist > radiusMi || timeMs === null || timeMs < since || timeMs > now + 5 * 60 * 1000) continue;
    const comment = typeof d.comment === "string" ? d.comment.trim().slice(0, 280) : "";
    rows.push({
      id: `ladder-${d.id}`,
      source: "ladder",
      type: String(d.condition || "Hazard report").slice(0, 60),
      magnitude: null,
      unit: null,
      city: null,
      county: null,
      state: null,
      remark: comment || null,
      reporter: "Ladder",
      lat: Math.round(rLat * 1e4) / 1e4,
      lng: Math.round(rLng * 1e4) / 1e4,
      distanceMi: Math.round(dist * 10) / 10,
      timeMs,
    });
  }
  return rows.sort((a, b) => b.timeMs - a.timeMs);
}

// Radar sites within `mi` of a point, nearest first, each with distanceMi.
export function sitesNear(sites, lat, lng, mi) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
  return sites
    .map((s) => ({ ...s, distanceMi: distanceMi(lat, lng, s.lat, s.lng) }))
    .filter((s) => s.distanceMi <= mi)
    .sort((a, b) => a.distanceMi - b.distanceMi);
}

// Where to pin an alert's name tag on the map: the middle (vertex average of
// the outer ring) of its polygon nearest the station, so a watch covering a
// dozen counties is labelled where the board is looking. [lng, lat] or null.
export function alertLabelPoint(geometry, lat, lng) {
  if (!geometry) return null;
  const polys =
    geometry.type === "Polygon" ? [geometry.coordinates]
      : geometry.type === "MultiPolygon" ? geometry.coordinates
        : [];
  let best = null;
  let bestD = Infinity;
  for (const rings of polys) {
    const ring = rings && rings[0];
    if (!Array.isArray(ring) || ring.length < 3) continue;
    const pts = ring.slice(0, -1);
    const cx = pts.reduce((sum, p) => sum + p[0], 0) / pts.length;
    const cy = pts.reduce((sum, p) => sum + p[1], 0) / pts.length;
    const d = Number.isFinite(lat) && Number.isFinite(lng) ? distanceMi(lat, lng, cy, cx) : 0;
    if (d < bestD) {
      bestD = d;
      best = [cx, cy];
    }
  }
  return best;
}

// Polygon approximating a circle of radiusMi around a point, for range rings.
export function circlePolygon(lat, lng, radiusMi, steps = 96) {
  const R = 3958.7613;
  const d = radiusMi / R;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lng * Math.PI) / 180;
  const ring = [];
  for (let i = 0; i <= steps; i += 1) {
    const θ = (2 * Math.PI * i) / steps;
    const φ2 = Math.asin(Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(θ));
    const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(d) * Math.cos(φ1), Math.cos(d) - Math.sin(φ1) * Math.sin(φ2));
    ring.push([(((λ2 * 180) / Math.PI + 540) % 360) - 180, (φ2 * 180) / Math.PI]);
  }
  return { type: "Polygon", coordinates: [ring] };
}

// IEM's NEXRAD base-reflectivity mosaic, as 5-minute frames going back 50
// minutes. `bucket` changes every 5 minutes so browsers and Mapbox fetch fresh
// frames instead of serving a cached, older image under the same URL.
export const RADAR_TILE_TEMPLATE =
  "https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913{frame}/{z}/{x}/{y}.png";
export function radarFrames({ loop = true, now = Date.now() } = {}) {
  const bucket = Math.floor(now / (5 * 60 * 1000));
  const minutes = loop ? [50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0] : [0];
  return minutes.map((m) => {
    const frame = m === 0 ? "" : `-m${String(m).padStart(2, "0")}m`;
    return {
      id: `radar-${bucket}-${m}`,
      minutesAgo: m,
      tiles: [`${RADAR_TILE_TEMPLATE.replace("{frame}", frame)}?v=${bucket}`],
    };
  });
}
